import React, { useState, useEffect } from 'react';
import '../css/App.css'; // Main CSS import
import API_BASE from './apiBase';
import Swal from 'sweetalert2';

const New_Request = ({ onSubmitSuccess, lockEmailToOutsideUser = true }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    contactPhone: '',
    requestType: [],
    additionalDetails: '',
    accountId: '',
    idDocument: null,
    confirmation: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOutsideUser, setIsOutsideUser] = useState(false);

  const getHeaders = () => {
    const apiKey = process.env.REACT_APP_MANAGER_API_KEY || localStorage.getItem('manager_api_key') || '';
    const apiToken = process.env.REACT_APP_MANAGER_API_TOKEN || localStorage.getItem('manager_api_token') || '';
    const headers = { 'Content-Type': 'application/json' };
    if (apiKey) headers['X-API-KEY'] = apiKey;
    if (apiToken) headers['X-API-TOKEN'] = apiToken;
    const tid = (
      localStorage.getItem('tenant_id') ||
      localStorage.getItem('dsar_tenant_id') ||
      localStorage.getItem('webapp_consent_tenant_id') ||
      localStorage.getItem('dsar_user_tenant_id') ||
      (window.USER && window.USER.tenant_id) ||
      (window.__USER__ && window.__USER__.tenant_id) ||
      new URLSearchParams(window.location.search || '').get('tenant_id') ||
      new URLSearchParams(window.location.search || '').get('tenant')
    );
    if (tid) headers['X-Tenant-ID'] = String(tid);
    return headers;
  };

  const buildApiCandidates = (path) => {
    const normalizedPath = String(path || '').replace(/^\/+/, '');
    const base = API_BASE ? String(API_BASE).replace(/\/$/, '') : '';
    return [...new Set([
      base && `${base}/${normalizedPath}`,
      base && `${base}/api/${normalizedPath}`,
      `/${normalizedPath}`,
      `/api/${normalizedPath}`,
      'http://localhost:8000/manager/dsar'
    ].filter(Boolean))];
  };

  // Auto-populate email for outside users on component mount
  useEffect(() => {
    if (!lockEmailToOutsideUser) {
      setIsOutsideUser(false);
      return;
    }

    try {
      const userType = localStorage.getItem('dsar_user_type');
      const userEmail = localStorage.getItem('dsar_user_email');

      if (userType === 'outside' && userEmail) {
        setIsOutsideUser(true);
        setFormData(prev => ({
          ...prev,
          email: userEmail.toLowerCase().trim()
        }));
      }
    } catch (e) {
      console.log('Error loading outside user info:', e);
    }
  }, [lockEmailToOutsideUser]);

  const handleRequestTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      requestType: prev.requestType.includes(type)
        ? prev.requestType.filter(t => t !== type)
        : [...prev.requestType, type]
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === 'file') {
      const file = files && files[0] ? files[0] : null;
      if (file) {
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        const isAllowedName = /\.(pdf|jpe?g|png)$/i.test(file.name || '');
        if (!allowedTypes.includes(file.type) && !isAllowedName) {
          setError('Please upload a PDF, JPG, JPEG, or PNG file.');
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          setError('File size must be 5MB or less.');
          return;
        }
      }
      setError(null);
      setFormData(prev => ({
        ...prev,
        [name]: file
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.confirmation) {
      Swal.fire({
        icon: 'warning',
        title: 'Confirmation Required',
        text: 'Please confirm the declaration',
        customClass: { popup: 'swal-popup-custom' }
      });
      return;
    }
    if (!formData.fullName || !formData.email) {
      Swal.fire({
        icon: 'warning',
        title: 'Required Fields Missing',
        text: 'Please fill in Full Name and Email',
        customClass: { popup: 'swal-popup-custom' }
      });
      return;
    }
    if (formData.contactPhone) {
      const phoneRegex = /^[6-9]\d{9}$/;

      if (!phoneRegex.test(formData.contactPhone)) {
        Swal.fire({
          icon: 'warning',
          title: 'Invalid Phone Number',
          text: 'Phone number must be 10 digits and start with 6, 7, 8, or 9',
          customClass: { popup: 'swal-popup-custom' }
        });
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        full_name: formData.fullName,
        email: formData.email.toLowerCase().trim(),
        contact_phone: formData.contactPhone,
        request_type: formData.requestType,
        request_details: formData.additionalDetails,
        personal_data_involved: formData.accountId,
        declaration_name: formData.fullName,
        declaration_date: new Date().toISOString().split('T')[0],
      };

      const searchParams = new URLSearchParams(window.location.search || '');
      const tenantIdCandidate = (
        searchParams.get('tenant_id') ||
        searchParams.get('tenantId') ||
        searchParams.get('tenant') ||
        localStorage.getItem('tenant_id') ||
        localStorage.getItem('dsar_tenant_id') ||
        localStorage.getItem('webapp_consent_tenant_id') ||
        localStorage.getItem('dsar_user_tenant_id') ||
        (window.USER && window.USER.tenant_id) ||
        (window.__USER__ && window.__USER__.tenant_id) ||
        null
      );
      if (tenantIdCandidate != null && tenantIdCandidate !== '') {
        const tenantId = Number(tenantIdCandidate);
        if (!Number.isNaN(tenantId) && tenantId > 0) {
          payload.tenant_id = tenantId;
        }
      }

      console.log('Submitting DSAR with payload:', payload);
      console.log('API Base:', API_BASE);

      const apiCandidates = buildApiCandidates('manager/dsar');
      let response = null;
      let responseData = null;
      let lastErr = null;
      const hasFile = formData.idDocument instanceof File;
      const selectedDrivePreference = (() => {
        try {
          return (window && window.localStorage && window.localStorage.getItem('consent_selected_drive')) || '';
        } catch (e) {
          return '';
        }
      })();

      for (const apiUrl of apiCandidates) {
        try {
          console.log('Posting to:', apiUrl);
          let requestOptions = {
            method: 'POST',
          };

          if (hasFile) {
            const form = new FormData();
            Object.keys(payload).forEach((key) => {
              const value = payload[key];
              if (value !== undefined && value !== null) {
                if (Array.isArray(value)) {
                  value.forEach((item) => form.append(key, item));
                } else {
                  form.append(key, String(value));
                }
              }
            });
            if (formData.idDocument) {
              form.append('attachments', formData.idDocument, formData.idDocument.name);
            }
            if (selectedDrivePreference) {
              form.append('drive_type', selectedDrivePreference);
            }
            const headers = getHeaders();
            delete headers['Content-Type'];
            if (selectedDrivePreference) {
              headers['X-Drive-Type'] = selectedDrivePreference;
            }
            const driveAccessToken = localStorage.getItem('drive_access_token') || '';
            if (driveAccessToken) headers['X-Google-Drive-Token'] = driveAccessToken;
            requestOptions = { ...requestOptions, headers, body: form };
          } else {
            const headers = getHeaders();
            requestOptions = { ...requestOptions, headers, body: JSON.stringify(payload) };
          }

          const res = await fetch(apiUrl, requestOptions);

          let parsed = null;
          try {
            const text = await res.text();
            parsed = text ? JSON.parse(text) : null;
          } catch (jsonErr) {
            parsed = null;
          }

          if (res.ok) {
            response = res;
            responseData = parsed || {};
            break;
          }

          // Retry on next candidate when endpoint/path is unavailable.
          if ([404, 405].includes(res.status)) {
            lastErr = new Error(`Endpoint unavailable at ${apiUrl} (HTTP ${res.status})`);
            continue;
          }

          const detail = (parsed && (parsed.detail || parsed.error || parsed.message)) || `Failed to submit request (Status: ${res.status})`;
          throw new Error(detail);
        } catch (err) {
          lastErr = err;
        }
      }

      if (!response) {
        throw lastErr || new Error('Failed to submit request');
      }

      console.log('Response status:', response.status);
      console.log('Response data:', responseData);

      if (!response.ok) {
        throw new Error(responseData.detail || responseData.message || `Failed to submit request (Status: ${response.status})`);
      }

      Swal.fire({
        icon: 'success',
        title: 'Request Submitted',
        text: 'Request submitted successfully!',
        customClass: { popup: 'swal-popup-custom' }
      });

      // Reset form
      setFormData({
        fullName: '',
        email: '',
        contactPhone: '',
        requestType: [],
        additionalDetails: '',
        accountId: '',
        idDocument: null,
        confirmation: false
      });

      // Callback to refresh dashboard
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    } catch (err) {
      console.error('Submission error:', err);
      const errMsg = err.message || 'Failed to submit request';
      setError(errMsg);
      const isDuplicate = typeof errMsg === 'string' && (errMsg.toLowerCase().includes('duplicate') || errMsg.toLowerCase().includes('already exist'));
      Swal.fire({
        icon: isDuplicate ? 'warning' : 'error',
        title: isDuplicate ? 'Duplicate Request' : 'Submission Failed',
        text: errMsg,
        customClass: { popup: 'swal-popup-custom' }
      });
    } finally {
      setLoading(false);
    }
  };

  const requestTypes = [
    {
      id: 'access',
      title: 'Right to Access Information',
      description: 'Request summary of personal data being processed and details of processing activities (Section 11(a))'
    },
    {
      id: 'correction',
      title: 'Right to Correction',
      description: 'Request correction of inaccurate or misleading personal data (Section 11(b))'
    },
    {
      id: 'updation',
      title: 'Right to Updation',
      description: 'Update incomplete personal data (Section 11(b))'
    },
    {
      id: 'erasure',
      title: 'Right to Erasure',
      description: 'Request erasure of personal data when no longer necessary or when consent is withdrawn (Section 11(c))'
    },
    {
      id: 'grievance',
      title: 'Right to Grievance Redressal',
      description: 'Register a complaint about data processing (Section 11(d))'
    },
    {
      id: 'nominate',
      title: 'Right to Nominate',
      description: 'Nominate another individual to exercise rights in case of death or incapacity (Section 11(e))'
    }
  ];

  return (
    <div className="new-request-page">
      <div className="new-request-container">
        {/* Header */}
        <div className="new-request-header">
          <div className="header-left">
            <div className="header-icon">📋</div>
            <div>
              <h1>Data Protection Request Form</h1>
              <p>Submit your request under the DPDP Act, 2023</p>
            </div>
          </div>
          <button type="button" className="new-request-back-btn" onClick={() => window.history.back()}>
            ← Back
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="new-request-form">
          {/* Personal Information Section */}
          <div className="form-section">
            <div className="section-header">
              <h2>Your Information</h2>
            </div>

            <div className="form-group">
              <label>Full Name <span className="required">*</span></label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                required
              />
            </div>

            <div className="form-group">
              <label>Email Address <span className="required">*</span></label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                readOnly={isOutsideUser}
                style={isOutsideUser ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
                required
              />
              {isOutsideUser && (
                <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                  Email is locked to your registered account
                </small>
              )}
            </div>

            <div className="form-group">
              <label>Contact Phone</label>
              {/* <input
                type="tel"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleInputChange}
                placeholder="Enter your contact phone"
              /> */}
              <input
                type="tel"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={(e) => {
                  let onlyNumbers = e.target.value.replace(/\D/g, '');

                  // Restrict to max 10 digits
                  if (onlyNumbers.length > 10) return;

                  // If first digit exists and is not 6-9, ignore it
                  if (onlyNumbers.length === 1 && !['6', '7', '8', '9'].includes(onlyNumbers)) {
                    return;
                  }

                  setFormData(prev => ({
                    ...prev,
                    contactPhone: onlyNumbers
                  }));
                }}
                placeholder="Enter your 10-digit mobile number"
                maxLength={10}
              />


            </div>
          </div>

          {/* Request Type Section */}
          <div className="form-section">
            <div className="section-header">
              <h2>Type of Request</h2>
            </div>
            <div className="section-subtitle">
              Under Section 11 of the DPDP Act, 2023, you have the following rights (Select all that apply):
            </div>

            <div className="checkbox-container">
              {requestTypes.map((type) => (
                <div
                  key={type.id}
                  className={`checkbox-item ${formData.requestType.includes(type.id) ? 'selected' : ''}`}
                  onClick={() => handleRequestTypeChange(type.id)}
                >
                  <div className="checkbox-wrapper">
                    <div className="custom-checkbox">
                      {formData.requestType.includes(type.id) && <span>✓</span>}
                    </div>
                    <div className="checkbox-content">
                      <div className="checkbox-title">{type.title}</div>
                      <div className="checkbox-description">{type.description}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Details Section */}
          <div className="form-section">
            <div className="section-header">
              <h2>Additional Details</h2>
            </div>

            <div className="form-group">
              <label>Please provide details about your request</label>
              <textarea
                name="additionalDetails"
                value={formData.additionalDetails}
                onChange={handleInputChange}
                placeholder="For example, specify which data you'd like to access, correct, or delete..."
                rows="4"
              />
            </div>

            <div className="form-group">
              <label>Account or Customer ID (if known)</label>
              <input
                type="text"
                name="accountId"
                value={formData.accountId}
                onChange={handleInputChange}
                placeholder="Enter your account or customer ID"
              />
            </div>
          </div>

          {/* Identity Verification Section */}
          <div className="form-section">
            <div className="section-header">
              <h2>Identity Verification</h2>
            </div>
            <div className="section-subtitle">
              As per DPDP Act requirements, we need to verify your identity before processing your request.
            </div>

            <div className="form-group">
              <label>Upload ID Document (Optional)</label>
              <div className="upload-area">
                <input
                  type="file"
                  name="idDocument"
                  onChange={handleInputChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                  id="file-upload"
                  style={{ display: 'none' }}
                />
                <label htmlFor="file-upload" className="upload-label">
                  <div className="upload-icon">📤</div>
                  <div className="upload-text">Click to upload or drag and drop</div>
                  <div className="upload-subtext">PDF, JPG, PNG (Max 5MB)</div>
                  {formData.idDocument && (
                    <div className="file-name">{formData.idDocument.name}</div>
                  )}
                </label>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="new-request-info-box">
            ℹ️ We may contact you via email or phone to verify your identity. If uploading Aadhaar,
            please ensure only the last 4 digits are visible as per UIDAI guidelines.
          </div>

          {/* Confirmation Section */}
          <div className="confirmation-section">
            <label className="confirmation-label">
              <input
                type="checkbox"
                name="confirmation"
                checked={formData.confirmation}
                onChange={handleInputChange}
              />
              <span>
                I confirm that the information provided is accurate and I am the Data Principal
                (or authorized representative) making this request under the DPDP Act, 2023
                <span className="required">*</span>
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? 'Submitting...' : 'Submit Request →'}
          </button>

          {error && <div style={{ color: 'red', marginTop: '10px' }}>{error}</div>}

          {/* Footer Text */}
          <div className="footer-text">
            We will respond to your request as per DPDP Act timelines.
            For grievances, you may also contact our Data Protection Officer.
          </div>
        </form>
      </div>
    </div>
  );
};

export default New_Request;